'use client';
import React, { useState } from 'react';

/**
 * Floating Accessibility Widget
 * - Toggles font size (normal/large)
 * - Toggles high-contrast mode
 * - Fully accessible and keyboard navigable
 */
const AccessibilityWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [largeFont, setLargeFont] = useState(false);
  const [highContrast, setHighContrast] = useState(false);

  // Apply accessibility classes to <body>
  React.useEffect(() => {
    document.body.classList.toggle('a11y-large-font', largeFont);
    document.body.classList.toggle('a11y-high-contrast', highContrast);
    return () => {
      document.body.classList.remove('a11y-large-font');
      document.body.classList.remove('a11y-high-contrast');
    };
  }, [largeFont, highContrast]);

  return (
    <>
      {/* Floating button */}
      <button
        aria-label={open ? 'Close accessibility menu' : 'Open accessibility menu'}
        onClick={() => setOpen((v) => !v)}
        style={{
          position: 'fixed',
          bottom: 24,
          left: 24,
          zIndex: 10000,
          width: 48,
          height: 48,
          borderRadius: '8px',
          background: 'transparent',
          color: '#888',
          border: 'none',
          boxShadow: 'none',
          fontSize: 32,
          cursor: 'pointer',
          padding: 0,
          transition: 'color 0.2s',
        }}
        tabIndex={0}
        onMouseOver={e => (e.currentTarget.style.color = '#222')}
        onMouseOut={e => (e.currentTarget.style.color = '#888')}
      >
        <span aria-hidden="true" role="img">♿</span>
      </button>
      {/* Modal menu */}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Accessibility options"
          tabIndex={-1}
          style={{
            position: 'fixed',
            bottom: 80,
            left: 24,
            zIndex: 10001,
            background: '#fff',
            color: '#222',
            borderRadius: 8,
            boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
            padding: 24,
            minWidth: 220,
          }}
        >
          <h2 style={{ fontSize: 18, margin: '0 0 12px 0' }}>Accessibility</h2>
          <div style={{ marginBottom: 12 }}>
            <label>
              <input
                type="checkbox"
                checked={largeFont}
                onChange={() => setLargeFont((v) => !v)}
              />{' '}
              Large font size
            </label>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label>
              <input
                type="checkbox"
                checked={highContrast}
                onChange={() => setHighContrast((v) => !v)}
              />{' '}
              High contrast
            </label>
          </div>
          <button
            onClick={() => setOpen(false)}
            style={{ marginTop: 8, background: '#222', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 16px', cursor: 'pointer' }}
          >
            Close
          </button>
        </div>
      )}
      <style jsx global>{`
        body.a11y-large-font {
          font-size: 1.25em !important;
        }
        body.a11y-high-contrast {
          background: #000 !important;
          color: #fff !important;
        }
        body.a11y-high-contrast a { color: #ff0 !important; }
        body.a11y-high-contrast button { background: #222 !important; color: #fff !important; }
      `}</style>
    </>
  );
};

export default AccessibilityWidget; 